/**
 * Utilidades para parsear y manipular URLs de imágenes
 * Centraliza la lógica de parsing para evitar duplicación de código
 */

import { defaultConfig } from 'app/config/environment';

function isS3Url(url) {
  return /\.s3(\.[^/]+)?\.amazonaws\.com\//i.test(url);
}

function buildS3ProxyUrl(originalUrl, size, format, token) {
  if (!token) return null;
  const guidMatch = originalUrl.match(/\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\//);
  if (!guidMatch) return null;
  const guid = guidMatch[1];

  const afterGuid = originalUrl.split(`/${guid}/`)[1].split('?')[0];
  const parts = afterGuid.split('/');
  const fileName = parts[parts.length - 1];
  const fileNameWithoutExtension = fileName.replace(/\.[^/.]+$/, '');
  const subPath = parts.slice(0, -1).join('/');

  const optimizedFile = `${fileNameWithoutExtension}_file_${size}.${format}`;
  const fullPath = subPath ? `${guid}/${subPath}/${optimizedFile}` : `${guid}/${optimizedFile}`;

  const { BASE_URL_ } = defaultConfig;
  return `${BASE_URL_}files-storage/open/${fullPath}?token=${token}`;
}

/**
 * Parsea una URL de imagen y extrae sus componentes
 * @param {string} originalUrl - URL original de la imagen (ej: https://s3.../guid/imagen.jpeg?token=...)
 * @returns {object|null} - Objeto con los componentes parseados o null si falla
 * @property {string} baseUrl - URL base sin el nombre del archivo
 * @property {string} fileName - Nombre completo del archivo con extensión
 * @property {string} fileNameWithoutExtension - Nombre del archivo sin extensión
 * @property {string} queryString - Query parameters con el prefijo '?' o cadena vacía
 */
export function parseImageUrl(originalUrl) {
  if (!originalUrl) return null;

  try {
    // Separar la URL base de los query parameters
    const [urlBase, queryParams] = originalUrl.split('?');
    const queryString = queryParams ? `?${queryParams}` : '';

    // Validar que la URL tenga suficientes segmentos
    const urlParts = urlBase.split('/');
    if (urlParts.length < 2) {
      return null;
    }

    // Obtener el nombre del archivo (último segmento, sin query params)
    const fileName = urlParts[urlParts.length - 1];

    // Quitar la extensión del archivo (ej: marquez.jpeg -> marquez)
    const fileNameWithoutExtension = fileName.replace(/\.[^/.]+$/, '');

    // Validar que fileNameWithoutExtension no esté vacío
    if (!fileNameWithoutExtension) {
      return null;
    }

    // Reconstruir la URL base sin el nombre del archivo
    const baseUrl = urlParts.slice(0, -1).join('/');

    return {
      baseUrl,
      fileName,
      fileNameWithoutExtension,
      queryString,
    };
  } catch (error) {
    console.error('imageUrlParser: Error parseando URL:', error);
    return null;
  }
}

/**
 * Genera una URL de imagen optimizada con el tamaño y formato especificados
 * @param {string} originalUrl - URL original de la imagen
 * @param {number} size - Tamaño de la imagen (ej: 400, 800, 1280)
 * @param {string} format - Formato de salida (ej: 'avif', 'webp')
 * @returns {string} - URL de la imagen optimizada
 */
export function generateOptimizedImageUrl(originalUrl, size, format, token) {
  const parsed = parseImageUrl(originalUrl);
  if (!parsed) return originalUrl;

  if (isS3Url(originalUrl)) {
    return buildS3ProxyUrl(originalUrl, size, format, token) || originalUrl;
  }

  const { baseUrl, fileNameWithoutExtension, queryString } = parsed;
  return `${baseUrl}/${fileNameWithoutExtension}_file_${size}.${format}${queryString}`;
}
